import { Observation, Patient } from '@medplum/fhirtypes'
import { ChartItem } from './PatientChart.utils.tsx'
import { Button } from '@mantine/core'
import { formatDateTime } from '@/utils.ts'
import { IconPlus } from '@tabler/icons-react'
import { ResourceTable } from '@/pages/Patient/components/ResourceTable.tsx'
import { useEffect } from 'react'
import { useScrollContext } from '@/context/ScrollContext'

type Props = {
  patient: Patient
  item: ChartItem
}

const availFields = [
  { code: 'date', title: 'Date' },
  { code: 'performer', title: 'Performer' },
  { code: 'result', title: 'Vital / Result' },
  { code: 'interpreter', title: '' },
]

// A UCUM annotation unit like `{score}` (e.g. pain 0–10) is a bare label, not
// a printable unit — suppress it so a pain reading shows "5", not "5 {score}".
const displayUnit = (unit: string | undefined): string =>
  unit && !unit.startsWith('{') ? ` ${unit}` : ''

const getValue = (observation: Observation): string => {
  if (observation.valueQuantity) {
    return `${observation.valueQuantity.value}${displayUnit(observation.valueQuantity.unit)}`
  } else if (observation.valueString) {
    return observation.valueString
  } else if (observation.valueCodeableConcept) {
    return (
      observation.valueCodeableConcept.text ||
      observation.valueCodeableConcept.coding?.[0]?.display ||
      '-'
    )
  } else if (observation.valueBoolean !== undefined) {
    return observation.valueBoolean ? 'Yes' : 'No'
  } else {
    return '-'
  }
}

const renderColumn = (observation: Observation, itemCode: string): string => {
  switch (itemCode) {
    case 'date':
      return observation.effectiveDateTime
        ? formatDateTime(observation.effectiveDateTime, '-', true)
        : '-'

    case 'performer':
      return observation.performer?.[0]?.display || '-'

    case 'result':
      return (
        observation.hasMember
          ?.map((item) => (item.resource as Observation)?.code?.coding?.[0].display || '-')
          .join('\n') || '-'
      )

    case 'interpreter':
      return (
        observation.hasMember?.map((item) => getValue(item.resource as Observation)).join('\n') ||
        '-'
      )
    default:
      return itemCode
  }
}

const preprocessList = (list: Observation[]): Observation[] => {
  const groups = list.filter((item) => item.code?.coding?.some((c) => c.code === '85353-1'))
  const included = list.filter((item) => item.code?.coding?.every((c) => c.code !== '85353-1'))
  const resourceList: Observation[] = []

  for (const group of groups) {
    const hasMember = []

    for (const member of group.hasMember || []) {
      let resource = included.find((item) => item.id === member.reference?.split('/')?.pop())
      if (!resource) {
        resource = group.contained?.find(
          (item) => item.id === member.reference?.split('/')?.pop(),
        ) as Observation | undefined
      }
      if (resource) {
        hasMember.push({
          ...member,
          resource,
        })
      }
    }

    group.hasMember = hasMember

    if (resourceList.some((item) => item.effectiveDateTime === group.effectiveDateTime)) {
      continue
    }
    resourceList.push(group)
  }

  return resourceList
}

export const Vitals = ({ patient, item }: Props) => {
  const { scrollToTop } = useScrollContext()

  useEffect(() => {
    scrollToTop()
  }, [])

  return (
    <div className="p-4">
      <div className="mb-3 flex items-center gap-4">
        <p className="text-2xl font-bold">Vitals</p>
        <Button
          variant="outline"
          className="!text-primary !border-[#D0E1F2] !bg-gradient-to-b from-[#F0F7FF] to-[#FFFFFF] !p-2 hover:from-[#FFFFFF]"
        >
          Add <IconPlus width={15} className="ml-1" />
        </Button>
      </div>

      <ResourceTable
        patient={patient}
        item={item}
        availFields={availFields}
        renderColumn={renderColumn}
        defaultSort="date"
        blockRemove
        preprocessList={preprocessList}
      />
    </div>
  )
}
